/**
 * HTTP Client with Authentication Interceptor for CNS Dashboard
 * Automatically adds JWT token to requests and handles token expiration
 */

import { authService } from '../services/authService';
import { API_URL } from '../config/apiConfig';

export interface RequestConfig extends RequestInit {
  skipAuth?: boolean; // Skip adding auth header
}

/**
 * Custom fetch wrapper with authentication interceptor
 */
export const httpClient = async (
  endpoint: string,
  config: RequestConfig = {}
): Promise<Response> => {
  const { skipAuth = false, ...fetchConfig } = config;

  // Prepare headers
  const headers = new Headers(fetchConfig.headers);

  // Add JWT token to Authorization header
  if (!skipAuth) {
    const token = authService.getToken();
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
  }

  // Add Content-Type if not set
  if (!headers.has('Content-Type') && fetchConfig.body) {
    headers.set('Content-Type', 'application/json');
  }

  // Build full URL
  const url = endpoint.startsWith('http') ? endpoint : `${API_URL}${endpoint}`;

  // Make request
  const response = await fetch(url, {
    ...fetchConfig,
    headers,
  });

  // Handle 401 Unauthorized - redirect to login
  if (response.status === 401 && !skipAuth) {
    // Clear auth data
    authService.logout();
    
    // Redirect to login page
    window.location.href = '/login';
    
    throw new Error('Unauthorized - redirecting to login');
  }

  return response;
};

/**
 * GET request helper
 */
export const get = async (endpoint: string, config?: RequestConfig): Promise<Response> => {
  return httpClient(endpoint, {
    ...config,
    method: 'GET',
  });
};

/**
 * POST request helper
 */
export const post = async (
  endpoint: string,
  body?: any,
  config?: RequestConfig
): Promise<Response> => {
  return httpClient(endpoint, {
    ...config,
    method: 'POST',
    body: body ? JSON.stringify(body) : undefined,
  });
};

/**
 * PUT request helper
 */
export const put = async (
  endpoint: string,
  body?: any,
  config?: RequestConfig
): Promise<Response> => {
  return httpClient(endpoint, {
    ...config,
    method: 'PUT',
    body: body ? JSON.stringify(body) : undefined,
  });
};

/**
 * PATCH request helper
 */
export const patch = async (
  endpoint: string,
  body?: any,
  config?: RequestConfig
): Promise<Response> => {
  return httpClient(endpoint, {
    ...config,
    method: 'PATCH',
    body: body ? JSON.stringify(body) : undefined,
  });
};

/**
 * DELETE request helper
 */
export const del = async (endpoint: string, config?: RequestConfig): Promise<Response> => {
  return httpClient(endpoint, {
    ...config,
    method: 'DELETE',
  });
};

export default {
  httpClient,
  get,
  post,
  put,
  patch,
  delete: del,
};
